import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { bnplMerchantApis } from "@payping/api";
import { CheckCircleFilled, Close } from "@payping/icons";
import { bnplMerchantGetContract } from "@payping/redux/actions";
import {
  Button,
  Checkbox,
  Drawer,
  Flex,
  Grid,
  Typography,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { FC, useRef, useState } from "react";

import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface Props {
  open: boolean;
  onClose: () => void;
}
const BnplActivationContractDrawer: FC<Props> = ({ onClose, open }) => {
  const { t } = useAppTranslation();
  const divRef = useRef<HTMLDivElement>(null);
  const { lg } = Grid.useBreakpoint();
  const [fileUrl, setFileUrl] = useState("");
  const contract = useAppSelector((state) => state.bnplMerchant.contract);
  const dispatch = useAppDispatch();
  const [contractState, getContract] = useMessagedAsyncFn(
    () => dispatch(bnplMerchantGetContract()),
    [],
    { loading: false },
    { error: true, success: false },
  );

  const [pdfPageNumber, setPdfPageNumber] = useState<number | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setPdfPageNumber(numPages);
  };

  const downloadContract = useMessagedAsync(
    async () => {
      if (!contract?.rawContractFileName) return undefined;
      const res = await bnplMerchantApis.getDownloadPath(contract?.rawContractFileName);
      try {
        if (res.url) {
          const response = await (await fetch(res.url)).blob();
          setFileUrl(URL.createObjectURL(response));
        }
      } catch (e) {
        return res;
      }

      return res;
    },
    [contract],
    { success: false, error: true },
  );

  const [signContractState, signContract] = useMessagedAsyncFn(
    (contractId: number) => bnplMerchantApis.merchantSignContract({ contractId }),
    [],
  );

  const [agree, setAgree] = useState(false);

  const bodyHeight = document.body.getBoundingClientRect().height;
  const drawerHeaderHeight = 65;
  const totalPaddings = 64;
  const OtherContent = 24 + 32;
  const totalGaps = 32 + 32;

  const handleFinish = async () => {
    const contractId = contract?.contractId;
    if (contractId) {
      await signContract(contractId);
      await getContract();
    }
  };

  const handleCloseDrawer = () => {
    if (signContractState.value) {
      getContract();
    }
    onClose();
  };

  const rect = divRef.current?.getBoundingClientRect();

  if (!open) return null;
  return (
    <Drawer
      closeIcon={false}
      height={"100%"}
      width={"100%"}
      placement="bottom"
      open={open}
      onClose={handleCloseDrawer}
      title={t("shop.bnpl.activation.contract.drawer.title")}
      extra={<Button type={"text"} icon={<Close width={14} height={14} />} onClick={handleCloseDrawer} />}
      classNames={{ body: "!tw-p-0" }}
    >
      {signContractState.value ? (
        <Flex vertical className="tw-mt-24 tw-w-full tw-p-8" gap={"large"} align="center">
          <CheckCircleFilled width={80} className="tw-fill-color-success" />
          <Typography.Title level={5} className="!tw-m-0">
            {t("shop.bnpl.activation.contract.drawer.success")}
          </Typography.Title>
        </Flex>
      ) : (
        <Flex vertical className="tw-h-full tw-w-full tw-p-8" gap={"32px"}>
          <Typography.Title level={5} className="!tw-m-0">
            {t("shop.bnpl.activation.contract.drawer.title2")}
          </Typography.Title>
          <div
            ref={divRef}
            className="tw-h-full tw-w-full tw-overflow-auto"
            style={{
              height:
                // 5 is for be sure
                bodyHeight - (drawerHeaderHeight + totalPaddings + OtherContent + totalGaps + 5),
            }}
          >
            {fileUrl && downloadContract.value ? (
              <Document file={fileUrl} onLoadSuccess={onDocumentLoadSuccess} loading={null} className={"tw-w-full"}>
                {Array.from(new Array(pdfPageNumber), (_, index) => (
                  <div key={`page_${index + 1}`}>
                    <Page pageNumber={index + 1} renderTextLayer={false} width={(rect?.width ?? 0) - (lg ? 100 : 0)} />
                  </div>
                ))}
              </Document>
            ) : null}
          </div>
          <Flex>
            <Checkbox checked={agree} onChange={(e: any) => setAgree(e.target.checked)}>
              {t("shop.bnpl.activation.contract.drawer.signContractCheck")}
            </Checkbox>
          </Flex>
          <Flex gap={"middle"}>
            <Button
              disabled={!agree}
              loading={signContractState.loading || contractState.loading}
              onClick={handleFinish}
              type="primary"
            >
              {t("shop.bnpl.activation.contract.drawer.confirm")}
            </Button>
          </Flex>
        </Flex>
      )}
    </Drawer>
  );
};

export default BnplActivationContractDrawer;
